/**
 * Historial de pedidos y cierre de cada venta (ADM-09, DOC-01).
 *
 * La columna que importa no es el total: es si el pedido ya tiene su boleta o
 * factura. El negocio las emite a mano en el SII, asi que un pedido pagado queda
 * "Falta boleta" hasta que alguien la sube, y eso es trabajo pendiente con un
 * cliente que ya pago: se marca en ambar y se aisla con el filtro "Por emitir".
 *
 * **Filtros y detalle (22-09-2026).** Buscador, pago, boleta o factura
 * (emitida o no), entrega, tipo de documento, fechas, total y orden, combinables y en la URL
 * (`utilidades/filtros-pedidos.ts`), como en Productos. Cada fila enlaza a
 * `?pedido=<id>`, que abre `ModalPedido.tsx` con todo lo que la tabla no
 * muestra y el formulario para subir el documento; el enlace se estira a la fila entera (`.fila-enlace`) porque la
 * fila no tiene otro control. Las columnas se ajustan a su texto: solo el
 * cliente puede bajar de linea (`.sin-cortes` en las demas). Abrir y cerrar reemplazan la entrada del
 * historial: el modal no es una pagina a la que se vuelva con "Atras".
 */
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import type { Pedido } from '@bandu/compartido';

import { BarraAdmin } from '../../componentes/administracion/BarraAdmin';
import { FiltrosActivos } from '../../componentes/administracion/FiltrosActivos';
import { FiltrosPedidos } from '../../componentes/administracion/FiltrosPedidos';
import { ModalPedido } from '../../componentes/administracion/ModalPedido';
import { Tabla } from '../../componentes/administracion/Tabla';
import { AvisoError, Cargando } from '../../componentes/comunes/EstadoPeticion';
import { Icono } from '../../componentes/comunes/Icono';
import { Paginador } from '../../componentes/catalogo/Paginador';
import { usePeticion } from '../../hooks/use-peticion';
import { Seo } from '../../seo/Seo';
import { obtenerPedidos } from '../../servicios/administracion.servicio';
import {
  conCambio,
  escribirFiltros,
  filtrosActivos,
  leerFiltros,
  PARAMETRO_PEDIDO,
  sinFiltros,
} from '../../utilidades/filtros-pedidos';
import type { FiltrosPedidos as Filtros } from '../../utilidades/filtros-pedidos';
import { textoEntrega } from '../../utilidades/entrega';
import { fechaHora, numero, pesos } from '../../utilidades/formato';
import { conPagina, rutas } from '../../utilidades/rutas';
import { ESTADO_PAGO, rotuloDocumento } from '../../utilidades/textos-pedido';

function idDe(valor: string | null): number | null {
  const id = Number(valor);
  return Number.isInteger(id) && id > 0 ? id : null;
}

export function Pedidos() {
  const [parametros, setParametros] = useSearchParams();
  const pagina = Number(parametros.get('pagina') ?? '1');
  const filtros = leerFiltros(parametros);
  // La cadena es la clave estable de los filtros: el objeto se rehace en cada render.
  const consultaFiltros = escribirFiltros(filtros).toString();
  const termino = filtros.termino ?? '';
  const pedidoAbierto = idDe(parametros.get(PARAMETRO_PEDIDO));

  const { estado, datos, error } = usePeticion(
    (senal) => obtenerPedidos({ ...filtros, pagina }, senal),
    [pagina, consultaFiltros],
  );

  const activos = filtrosActivos(filtros);

  // Subir un documento devuelve el pedido actualizado: la fila se pinta con el,
  // sin volver a pedir la pagina.
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  useEffect(() => {
    if (datos !== null) setPedidos(datos.items);
  }, [datos]);

  // Cambiar un filtro vuelve a la pagina 1 y cierra el modal.
  function aplicarFiltros(siguientes: Filtros): void {
    setParametros(escribirFiltros(siguientes));
  }

  function buscar(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault();
    const valor = new FormData(evento.currentTarget).get('termino');
    const texto = typeof valor === 'string' ? valor.trim() : '';
    aplicarFiltros(conCambio(filtros, { termino: texto.length > 0 ? texto : undefined }));
  }

  const rutaListado =
    consultaFiltros.length > 0
      ? `${rutas.administracion.pedidos()}?${consultaFiltros}`
      : rutas.administracion.pedidos();
  const rutaActual = conPagina(rutaListado, pagina);

  /** La URL de hoy con el modal de `id` abierto (pagina y filtros intactos). */
  function rutaConPedido(id: number): string {
    const siguientes = new URLSearchParams(parametros);
    siguientes.set(PARAMETRO_PEDIDO, String(id));
    return `?${siguientes.toString()}`;
  }

  function cerrarPedido(): void {
    const siguientes = new URLSearchParams(parametros);
    siguientes.delete(PARAMETRO_PEDIDO);
    setParametros(siguientes, { replace: true });
  }

  return (
    <>
      <Seo
        titulo="Pedidos"
        descripcion="Historial de pedidos y sus boletas o facturas."
        ruta={rutas.administracion.pedidos()}
        sinIndexar
      />

      <BarraAdmin
        titulo="Pedidos"
        bajada={
          datos === null
            ? undefined
            : activos.length > 0
              ? `${numero(datos.total)} cumplen los filtros`
              : `${numero(datos.total)} en el historial`
        }
        acciones={
          <form onSubmit={buscar} role="search">
            <label className="solo-lectores" htmlFor="buscar-pedidos">
              Buscar pedidos por referencia, cliente, correo, telefono, RUT, folio, comuna o codigo de producto
            </label>
            {/* `key`: si un chip quita la busqueda, el campo se vacia. */}
            <input
              key={termino}
              id="buscar-pedidos"
              name="termino"
              type="search"
              className="entrada"
              placeholder="Referencia, cliente, RUT, folio..."
              defaultValue={termino}
            />
            <button type="submit" className="btn btn-sm btn-primario">
              <Icono nombre="buscar" />
              Buscar
            </button>
          </form>
        }
      />

      <div className="admin-cuerpo">
        <FiltrosPedidos filtros={filtros} onCambiar={aplicarFiltros} />
        <FiltrosActivos
          activos={activos}
          onCambiar={aplicarFiltros}
          onLimpiar={() => {
            aplicarFiltros(sinFiltros(filtros));
          }}
        />

        {estado === 'cargando' ? <Cargando que="los pedidos" /> : null}
        {estado === 'error' ? <AvisoError error={error} /> : null}

        {datos !== null && datos.items.length === 0 ? (
          <div className="panel-vacio" role="status">
            <p>
              {activos.length > 0
                ? 'Ningun pedido cumple estos filtros.'
                : 'Todavia no hay pedidos.'}
            </p>
            {activos.length > 0 ? (
              <button
                type="button"
                className="btn btn-sm btn-contorno"
                onClick={() => {
                  aplicarFiltros(sinFiltros(filtros));
                }}
              >
                Limpiar filtros
              </button>
            ) : null}
          </div>
        ) : null}

        {datos !== null && datos.items.length > 0 ? (
          <>
            <Tabla
              titulo="Historial de pedidos"
              encabezados={
                <>
                  <th scope="col">Pedido</th>
                  <th scope="col">Cliente</th>
                  <th scope="col">Entrega</th>
                  <th scope="col" className="der">
                    Total
                  </th>
                  <th scope="col">Pago</th>
                  <th scope="col">Boleta o factura</th>
                </>
              }
            >
              {pedidos.map((pedido) => {
                const pago = ESTADO_PAGO[pedido.estado];
                const documento = rotuloDocumento(pedido);
                const unidades = pedido.items.reduce((suma, item) => suma + item.cantidad, 0);
                return (
                  <tr
                    key={pedido.id}
                    className={pedido.id === pedidoAbierto ? 'fila-enlace fila-seleccionada' : 'fila-enlace'}
                  >
                    <th scope="row" className="sin-cortes">
                      <Link className="enlace-fila" to={rutaConPedido(pedido.id)} replace>
                        <span className="mono">{pedido.referencia}</span>
                        <span className="solo-lectores">, ver el detalle</span>
                      </Link>
                      <span className="s">
                        <time dateTime={pedido.creadoEn}>{fechaHora(pedido.creadoEn)}</time>
                      </span>
                    </th>
                    <td className="partible">
                      {pedido.contacto.nombre}
                      <span className="s">
                        {numero(unidades)} {unidades === 1 ? 'producto' : 'productos'}
                      </span>
                    </td>
                    <td className="sin-cortes">
                      {textoEntrega(pedido.tipoEntrega)}
                      {pedido.direccion !== null ? (
                        <span className="s">{pedido.direccion.comuna}</span>
                      ) : null}
                    </td>
                    <td className="der num">{pesos(pedido.total)}</td>
                    <td className="sin-cortes">
                      <span className={pago.clase}>{pago.texto}</span>
                    </td>
                    <td className="sin-cortes">
                      <span className={documento.clase}>{documento.texto}</span>
                    </td>
                  </tr>
                );
              })}
            </Tabla>

            <Paginador
              pagina={datos.pagina}
              total={datos.total}
              porPagina={datos.porPagina}
              ruta={rutaListado}
            />
          </>
        ) : null}
      </div>

      {/* Volver desde la ficha de un producto reabre este mismo pedido. */}
      <ModalPedido
        id={pedidoAbierto}
        volver={
          pedidoAbierto !== null
            ? `${rutaActual}${rutaActual.includes('?') ? '&' : '?'}${PARAMETRO_PEDIDO}=${pedidoAbierto}`
            : rutaActual
        }
        onCerrar={cerrarPedido}
        onDocumentoSubido={(actualizado) => {
          setPedidos((actuales) =>
            actuales.map((pedido) => (pedido.id === actualizado.id ? actualizado : pedido)),
          );
        }}
      />
    </>
  );
}
